import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { useViewportHeight, useIOSKeyboardFix } from 'utils/responsiveUtils';

// Lazy loading para optimización de rendimiento
import {
  HomePage,
  ProductoSOS,
  Packs,
  Comprar,
  Activar,
  TutorialActivacion,
  Panel,
  FAQ,
  Soporte,
  Contacto,
  Privacidad,
  ARCOSolicitudes,
  Terminos,
  CookiePolicy,
  LegalVersions,
  RetractoForm,
  GarantiaForm,
  ConsultarTicket,
  PerfilPublico,
  PublicProfile,
  OnboardingPage,
  PageFallback
} from './components/lazy/LazyPages';

// Componentes de activación
import WebIDActivationFlow from './components/activation/WebIDActivationFlow';
import WebIDOnboarding from './components/onboarding/WebIDOnboarding';
import UserDashboard from './components/dashboard/UserDashboard';
import EditEmergencyProfile from './components/dashboard/EditEmergencyProfile';
import AdminUserDashboard from './components/admin/AdminUserDashboard';
import ActivationGuard, { AuthGuard } from './components/guards/ActivationGuard';

// Componentes específicos del flujo SOS
import ClaimWebID from './pages/ClaimWebID';
import PanelPage from './pages/PanelPage';

import LazyLoad from './components/common/LazyLoad';
import BottomNav from 'components/layout/BottomNav';
import ErrorBoundary from 'components/shared/ErrorBoundary';
import NotFoundPage from 'components/shared/NotFoundPage';
import ConnectionStatus from 'components/ui/ConnectionStatus';
import CookieConsentBanner from 'components/cookies/CookieConsentBanner';
import LoadingSpinner from 'components/ui/LoadingSpinner';
import AdminDashboard from 'pages/admin/AdminDashboard';
import Login from 'pages/auth/Login';
import Register from 'pages/auth/Register';
import ResetPassword from 'pages/auth/ResetPassword';

// Módulo SOS Global
import SOSPage from 'pages/SOSPage';
import SOSStickyBar from 'components/sos/SOSStickyBar';
import { useSOSStickyBar } from 'hooks/useSOSStickyBar';

// Componente para rutas protegidas
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
    if (loading) {
    // Mostrar un indicador de carga mientras se verifica la autenticación
    return (
      <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
        <LoadingSpinner size="lg" message="Verificando sesión..." />
      </div>
    );
  }
  
  if (!user) {
    // Redirigir a login si no hay usuario autenticado
    return <Navigate to="/auth/login" replace />;
  }
  
  if (requireAdmin && user.role !== 'admin') {
    // Redirigir a dashboard si se requiere admin pero el usuario no lo es
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// Aplicación principal
const App = () => {
  // Aplicar hooks de responsive
  useViewportHeight();
  useIOSKeyboardFix();

  // Hook para gestionar barra sticky SOS
  const { isVisible: showSOSBar } = useSOSStickyBar();

  // Analytics tracking para SOS
  const handleSOSClick = (service: string, country: string, location: string) => {
    // GA4/Matomo event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sos_click', {
        country: country,
        service: service,
        location: location
      });
    }

    // Matomo tracking (si está disponible)
    if (typeof window !== 'undefined' && (window as any)._paq) {
      (window as any)._paq.push(['trackEvent', 'SOS', 'Click', `${country}-${service}-${location}`]);
    }

    console.log('SOS Click:', { service, country, location });
  };
  
  return (
    <ErrorBoundary>
      <Routes>
        {/* Rutas de autenticación */}
        <Route
          path="/auth/login"
          element={
            <ErrorBoundary>
              <Login />
            </ErrorBoundary>
          }
        />
        <Route
          path="/auth/register"
          element={
            <ErrorBoundary>
              <Register />
            </ErrorBoundary>
          }
        />
        <Route path="/register" element={<Navigate to="/auth/register" replace />} />
        <Route
          path="/auth/reset-password"
          element={
            <ErrorBoundary>
              <ResetPassword />
            </ErrorBoundary>
          }
        />

        {/* Rutas protegidas - Panel de usuario */}
        <Route
          path="/panel/*"
          element={
            <ErrorBoundary>
              <ProtectedRoute>
                <PanelPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />

        {/* Rutas administrativas */}
        <Route
          path="/admin/*"
          element={
            <ErrorBoundary>
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />

        {/* Página de inicio pública */}
        <Route
          path="/"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <HomePage />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Módulo SOS Global - CRÍTICO para emergencias */}
        <Route
          path="/sos"
          element={
            <ErrorBoundary>
              <SOSPage />
            </ErrorBoundary>
          }
        />

        {/* Rutas comerciales públicas */}
        <Route
          path="/producto/sos"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <ProductoSOS />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/packs"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Packs />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        {/* Redirección de la ruta antigua para compatibilidad */}
        <Route path="/planes" element={<Navigate to="/packs" replace />} />
        <Route
          path="/comprar"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Comprar />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/activar"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Activar />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/tutorial-activacion"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <TutorialActivacion />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Panel de gestión familiar/B2B */}
        <Route
          path="/panel"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Panel />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Páginas de soporte */}
        <Route
          path="/faq"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <FAQ />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/soporte"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Soporte />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/privacidad"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Privacidad />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/privacidad/solicitudes"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <ARCOSolicitudes />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/terminos"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Terminos />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/cookies"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <CookiePolicy />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/legal/versiones"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <LegalVersions />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/ayuda/retracto"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <RetractoForm />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/ayuda/garantia"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <GarantiaForm />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/ayuda/consultar-ticket"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <ConsultarTicket />
              </LazyLoad>
            </ErrorBoundary>
          }
        />
        <Route
          path="/contacto"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <Contacto />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Perfil público de emergencia - Optimización crítica */}
        <Route
          path="/w/:webId"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <PublicProfile />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Ruta de activación por claim token */}
        <Route
          path="/claim/:webId"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <ClaimWebID />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Ruta de onboarding post-asignación */}
        <Route
          path="/onboarding/:webId"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <OnboardingPage />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Perfil público legacy - Redirección */}
        <Route
          path="/id/:token"
          element={
            <ErrorBoundary>
              <LazyLoad fallback={<PageFallback />}>
                <PerfilPublico />
              </LazyLoad>
            </ErrorBoundary>
          }
        />

        {/* Perfil público con guard */}
        <Route
          path="/profile/:webId"
          element={
            <ErrorBoundary>
              <ActivationGuard guardType="profile">
                <LazyLoad fallback={<PageFallback />}>
                  <PublicProfile />
                </LazyLoad>
              </ActivationGuard>
            </ErrorBoundary>
          }
        />

        {/* Rutas de activación de WebID */}
        <Route
          path="/activate/:webId"
          element={
            <ErrorBoundary>
              <ActivationGuard guardType="activation">
                <WebIDActivationFlow />
              </ActivationGuard>
            </ErrorBoundary>
          }
        />

        <Route
          path="/onboarding/:webId"
          element={
            <ErrorBoundary>
              <ActivationGuard guardType="onboarding">
                <WebIDOnboarding />
              </ActivationGuard>
            </ErrorBoundary>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ErrorBoundary>
              <AuthGuard>
                <UserDashboard />
              </AuthGuard>
            </ErrorBoundary>
          }
        />

        {/* Ruta para editar perfil de emergencia desde dashboard de usuario */}
        <Route
          path="/dashboard/profile/:webId/edit"
          element={
            <ErrorBoundary>
              <AuthGuard>
                <EditEmergencyProfile />
              </AuthGuard>
            </ErrorBoundary>
          }
        />

        {/* Ruta para que administradores vean dashboard de usuario específico */}
        <Route
          path="/admin/user-dashboard/:userId"
          element={
            <ErrorBoundary>
              <AuthGuard>
                <AdminUserDashboard />
              </AuthGuard>
            </ErrorBoundary>
          }
        />

        {/* Ruta 404 para páginas no encontradas */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Navegación inferior para móviles */}
      <BottomNav />

      {/* Barra sticky SOS - CRÍTICO para emergencias */}
      <SOSStickyBar
        hidden={!showSOSBar}
        onSOSClick={handleSOSClick}
      />

      {/* Estado de conexión */}
      <ConnectionStatus />

      {/* Banner de consentimiento de cookies */}
      <CookieConsentBanner />
    </ErrorBoundary>
  );
};

export default App;
